import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import { clientiApi } from '../services/api';
import api from '../services/api';

// ── Ordinamento colonne ─────────────────────────────────────
function sortData(data, col, dir) {
  return [...(data||[])].sort((a, b) => {
    let va = a[col] != null ? a[col] : '';
    let vb = b[col] != null ? b[col] : '';
    if (!isNaN(parseFloat(va)) && !isNaN(parseFloat(vb))) {
      va = parseFloat(va); vb = parseFloat(vb);
    } else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
    return dir === 'asc' ? (va > vb ? 1 : va < vb ? -1 : 0) : (va < vb ? 1 : va > vb ? -1 : 0);
  });
}

function SortTh({ col, label, align, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th onClick={() => onSort(col)} style={{
      padding:'8px 10px', background:'#f1f5f9', borderBottom:'2px solid #e5e7eb',
      fontSize:11, fontWeight:600, whiteSpace:'nowrap', textAlign:align||'left',
      cursor:'pointer', userSelect:'none',
    }}>
      {label}{' '}<span style={{color:active?'#b30000':'#ccc',fontSize:10}}>
        {active ? (sortDir==='asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

function useSortState(defaultCol, defaultDir) {
  const [sortCol, setSortCol] = React.useState(defaultCol || 'date');
  const [sortDir, setSortDir] = React.useState(defaultDir || 'desc');
  const onSort = React.useCallback((col) => {
    setSortCol(prev => {
      if (prev === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return prev; }
      setSortDir('desc');
      return col;
    });
  }, []);
  return { sortCol, sortDir, onSort };
}


// ── API contratti ──────────────────────────────────────────────
const contrattiApi = {
  getAll:   (stato='') => api.get(`/contratti${stato?`?stato=${stato}`:''}`),
  getById:  id         => api.get(`/contratto/${id}`),
  create:   data       => api.post('/contratti', data),
  update:   (id, data) => api.post(`/contratto/${id}`, data),
  fattura:  (id, data) => api.post(`/contratto/${id}/fattura`, data),
};

const eur = n => '€ ' + parseFloat(n||0).toLocaleString('it-IT',{minimumFractionDigits:2});
const fmtData = d => (!d || d==='0000-00-00') ? '—' : d.substring(0,10).split('-').reverse().join('/');

const IMPIANTI_LABEL = {
  estintori:'🧯 Estintori', idranti:'🚒 Idranti/Naspi', porte:'🚪 Porte REI/U.S./Portoni scorrevoli',
  rivelazione_fumi:'💨 Rilevazione fumi', sprinkler:'💧 Sprinkler',
  illuminazione_emergenza:'💡 Luci di emergenza',
  gruppi_pompa:'⚙️ Gruppi pompa', cassette_mediche:'🩺 Cassette mediche',
  dpi:'🦺 DPI', efc:'⚡ EFC', filtri:'🔧 Filtri',
  prove_flusso:'🌊 Prove flusso', gruppi_elettrogeni:'🔋 Gruppi elettrogeni',
};

const PREZZI_DEFAULT = {
  estintori:3.00, idranti:4.65, porte:4.65, rivelazione_fumi:170.00,
  sprinkler:150.00, gruppi_pompa:100.00, cassette_mediche:20.00,
  dpi:20.00, efc:30.00, filtri:30.00, prove_flusso:70.00, gruppi_elettrogeni:50.00,
};

// ── COMPONENTE PRINCIPALE ──────────────────────────────────────
// ── Ordinamento colonne ────────────────────────────────────────
export default function ContrattiPage() {
  const { id: idUrl } = useParams();
  const navigate = useNavigate();
  const [vista, setVista] = useState(idUrl ? 'dettaglio' : 'lista'); // lista | dettaglio | nuovo
  const [idSel, setIdSel] = useState(idUrl ? Number(idUrl) : null);

  // Segue l'indirizzo: arrivando da un'altra pagina apre il contratto indicato
  useEffect(() => {
    if (idUrl) { setIdSel(Number(idUrl)); setVista('dettaglio'); }
  }, [idUrl]);

  const apri  = (id) => { setIdSel(id); setVista('dettaglio'); navigate(`/contratti/${id}`); };
  const torna = ()   => { setVista('lista'); setIdSel(null); navigate('/contratti'); };

  if (vista==='dettaglio') return <DettaglioContratto id={idSel} onBack={torna} />;
  if (vista==='nuovo')     return <NuovoContratto onBack={torna} onSaved={apri} />;
  return <ListaContratti onApri={apri} onNuovo={()=>setVista('nuovo')} />;
}

// ── LISTA ──────────────────────────────────────────────────────
function ListaContratti({ onApri, onNuovo }) {
  const [contratti, setContratti] = useState([]);
  const { sortCol, sortDir, onSort } = useSortState('data_inizio', 'desc');
  const contrattiSorted = React.useMemo(() => sortData(contratti, sortCol, sortDir), [contratti, sortCol, sortDir]);
  const [loading, setLoading]     = useState(true);
  const [filtroStato, setFiltro]  = useState('');
  const [q, setQ]                 = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contrattiApi.getAll(filtroStato);
      setContratti(res.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtroStato]);

  useEffect(() => { carica(); }, [carica]);

  const filtrati = contrattiSorted.filter(c =>
    !q || (c.cliente_nome||c.cliente_id_raw||'').toLowerCase().includes(q.toLowerCase()) ||
           (c.numero_protocollo||'').toLowerCase().includes(q.toLowerCase())
  );

  const badgeScad = (c) => {
    const g = c.giorni_scadenza;
    if (g===null||g===undefined||!c.data_fine||c.data_fine==='0000-00-00')
      return <span className="badge badge-gray">—</span>;
    if (g < 0)   return <span className="badge badge-red">Scaduto {Math.abs(g)}gg fa</span>;
    if (g <= 60) return <span className="badge badge-orange">⚠️ {g} giorni</span>;
    return <span className="badge badge-green">{g} giorni</span>;
  };

  const nImp = c => ['estintori','idranti','porte','fumi','sprinkler','efc','filtri','gruppi','emergenza']
    .reduce((s,k)=>s+parseInt(c[k]||0),0);

  return (
    <div className="page-content">
      <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <input className="form-control" style={{maxWidth:260}}
          placeholder="🔍 Cliente o protocollo..."
          value={q} onChange={e=>setQ(e.target.value)} />
        <select className="form-control" style={{maxWidth:160}}
          value={filtroStato} onChange={e=>setFiltro(e.target.value)}>
          <option value="">Tutti</option>
          <option value="attivo">Attivi</option>
          <option value="scaduto">Scaduti</option>
          <option value="rescisso">Rescissi</option>
        </select>
        <button className="btn btn-primary" onClick={onNuovo}>➕ Nuovo contratto</button>
        <span style={{marginLeft:'auto',alignSelf:'center',color:'#9ca3af',fontSize:13}}>
          {filtrati.length} contratti
        </span>
      </div>

      {loading ? <div className="loading"><div className="spinner"></div></div> : (
        <div className="card" style={{padding:0}}>
          <div className="table-wrap">
            <table className="q-table">
              <thead><tr>
                <SortTh col='numero_protocollo' label='Protocollo' sortCol={sortCol} sortDir={sortDir} onSort={onSort} /><SortTh col='cliente_nome' label='Cliente / Sede' sortCol={sortCol} sortDir={sortDir} onSort={onSort} /><SortTh col='data_inizio' label='Inizio' sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <SortTh col='data_fine' label='Scadenza' sortCol={sortCol} sortDir={sortDir} onSort={onSort} /><th>Impianti</th><SortTh col='stato' label='Stato' sortCol={sortCol} sortDir={sortDir} onSort={onSort} /><SortTh col='accettato' label='Accettato' sortCol={sortCol} sortDir={sortDir} onSort={onSort} /><th></th>
              </tr></thead>
              <tbody>
                {filtrati.length===0 && (
                  <tr><td colSpan={8} style={{textAlign:'center',color:'#9ca3af',padding:32}}>Nessun contratto</td></tr>
                )}
                {filtrati.map(c=>(
                  <tr key={c.id} style={{cursor:'pointer'}} onClick={()=>onApri(c.id)}>
                    <td><strong>{c.numero_protocollo||'#'+c.id}</strong></td>
                    <td>
                      <div style={{fontWeight:600}}>{c.cliente_nome||'—'}</div>
                      {(()=>{
                        const legale=[c.cliente_indirizzo,[c.cliente_cap,c.cliente_citta].filter(Boolean).join(' '),c.cliente_provincia?`(${c.cliente_provincia})`:''].filter(Boolean).join(', ');
                        const testo=c.sede_indirizzo||legale;
                        if(!testo) return null;
                        const altre=(c.sede_n||0)>1?c.sede_n-1:0;
                        return (
                          <div style={{fontSize:11,color:'#9ca3af'}}>
                            {testo}
                            {altre>0 && <span style={{marginLeft:4}}>+{altre}</span>}
                            {!c.sede_indirizzo && <span style={{marginLeft:4,fontStyle:'italic'}}>· sede legale</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{fontSize:12}}>{fmtData(c.data_inizio)}</td>
                    <td>
                      <div style={{fontSize:12}}>{fmtData(c.data_fine)}</div>
                      {badgeScad(c)}
                    </td>
                    <td style={{textAlign:'center'}}>
                      {nImp(c)>0 ? <span className="badge badge-blue">{nImp(c)} imp.</span> : '—'}
                    </td>
                    <td><span className={`badge ${c.stato==='attivo'?'badge-green':c.stato==='scaduto'?'badge-red':'badge-gray'}`}>
                      {c.stato||'attivo'}
                    </span></td>
                    <td>
                      {c.accettato
                        ? <span className="badge badge-green">✅ Accettato</span>
                        : <span className="badge" style={{background:'#fef9c3',color:'#854d0e'}}>⏳ In attesa</span>}
                    </td>
                    <td><button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();onApri(c.id);}}>Apri →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── DETTAGLIO ──────────────────────────────────────────────────
function DettaglioContratto({ id, onBack }) {
  const navigate = useNavigate();
  const vaiAlCliente = (cid) => cid && navigate(`/clienti/${cid}`);
  const [data, setData]   = useState(null);
  const [loading, setLoad]= useState(true);
  const [editDate, setED] = useState(false);
  const [dateForm, setDF] = useState({data_inizio:'',data_fine:''});
  const [saving, setSave] = useState(false);

  useEffect(()=>{
    contrattiApi.getById(id)
      .then(r=>{ setData(r.data); setDF({data_inizio:r.data.contratto?.data_inizio?.substring(0,10)||'',data_fine:r.data.contratto?.data_fine?.substring(0,10)||'',protocollo_interno:r.data.contratto?.protocollo_interno||''}); })
      .catch(console.error)
      .finally(()=>setLoad(false));
  },[id]);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!data)   return <div className="page-content"><p style={{color:'red'}}>Contratto non trovato.</p></div>;

  const { contratto:c, righe=[], sedi=[], interventi=[] } = data;
  const totTrim = righe.filter(r=>r.frequenza==='trimestrale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const totSem = righe.filter(r=>r.frequenza==='semestrale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const totAnn = righe.filter(r=>r.frequenza==='annuale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const g = c.giorni_scadenza;
  const hasDate = c.data_fine && c.data_fine!=='0000-00-00';

  const salvaDate = async () => {
    setSave(true);
    try {
      await contrattiApi.update(id, {
        data_inizio: dateForm.data_inizio,
        data_fine:   dateForm.data_fine,
        protocollo_interno: dateForm.protocollo_interno || null,
      });
      const r = await contrattiApi.getById(id);
      setData(r.data);
      setED(false);
    } catch(e){console.error(e);}
    finally{setSave(false);}
  };

  const eliminaContratto = async () => {
    if (!window.confirm(`Eliminare il contratto ${c?.numero_protocollo || '#'+id}?
Verranno eliminati anche tutte le righe e la pianificazione.`)) return;
    if (!window.confirm('Conferma definitiva: eliminare il contratto?')) return;
    try {
      await api.delete(`/contratto/${id}`);
      onBack();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const fatturaIntervento = async (ci) => {
    if (!window.confirm(`Emettere fattura per intervento ${ci.tipo}?`)) return;
    try {
      const res = await contrattiApi.fattura(id,{tipo:ci.tipo,intervento_pianificato_id:ci.id});
      if (res.data?.success) {
        alert(`✅ Fattura emessa! N° ${res.data.numero||'—'}`);
        const r = await contrattiApi.getById(id);
        setData(r.data);
      }
    } catch(e){ alert('Errore: '+e.message); }
  };

  return (
    <div className="page-content">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <button className="btn btn-secondary" onClick={onBack}>⬅ Lista contratti</button>
        <h2 style={{margin:0,fontSize:18}}>{c.numero_protocollo||'#'+c.id}</h2>
        <span className={`badge ${c.stato==='attivo'?'badge-green':c.stato==='scaduto'?'badge-red':'badge-gray'}`}>{c.stato||'attivo'}</span>
        {hasDate && g!==null && (
          g<0 ? <span className="badge badge-red">Scaduto {Math.abs(g)}gg fa</span>
              : g<=60 ? <span className="badge badge-orange">⚠️ Scade tra {g} giorni</span>
              : <span className="badge badge-green">Scade tra {g} giorni</span>
        )}
        <button className="btn btn-danger btn-sm" style={{marginLeft:'auto'}} onClick={eliminaContratto}>
          🗑 Elimina contratto
        </button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,maxWidth:900}}>

        {/* INFO */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">📋 Informazioni</span>
            <button className="btn btn-secondary btn-sm" onClick={()=>setED(v=>!v)}>
              {editDate?'Annulla':'✏️ Modifica date'}
            </button>
          </div>
          <table style={{width:'100%',fontSize:13}}>
            <tbody>
              <tr><td style={{color:'#9ca3af',padding:'4px 0',width:'40%'}}>Cliente</td>
                  <td>
                    {c.cliente_id ? (
                      <span onClick={()=>vaiAlCliente(c.cliente_id)}
                        style={{cursor:'pointer',color:'#b30000',fontWeight:700,
                                textDecoration:'underline'}}
                        title="Apri la scheda del cliente">
                        {c.cliente_nome||'—'} ↗
                      </span>
                    ) : <strong>{c.cliente_nome||'—'}</strong>}
                  </td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0',verticalAlign:'top'}}>Indirizzo</td>
                  <td>
                    {c.cliente_indirizzo ? (
                      <div style={{fontSize:13,lineHeight:1.5}}>
                        <div>{c.cliente_indirizzo}</div>
                        <div>{[c.cliente_cap, c.cliente_citta].filter(Boolean).join(' ')} {c.cliente_provincia?`(${c.cliente_provincia})`:''}</div>
                      </div>
                    ) : <IndirizzoCliente clienteId={c.cliente_id} />}
                  </td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0',verticalAlign:'top'}}>Referente</td>
                  <td>
                    <ReferenteEditor clienteId={c.cliente_id} campo="contatto" />
                  </td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0',verticalAlign:'top'}}>Telefono</td>
                  <td>
                    <ReferenteEditor clienteId={c.cliente_id} campo="telefono" />
                  </td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Accettato</td>
                  <td>
                    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                      <input type="checkbox" checked={!!c.accettato}
                        onChange={async e=>{
                          await contrattiApi.update(id,{accettato:e.target.checked?1:0});
                          const r=await contrattiApi.getById(id);setData(r.data);
                        }} style={{width:16,height:16,accentColor:'#b30000'}} />
                      <span style={{fontSize:13}}>{c.accettato?'✅ Sì':'⏳ In attesa'}</span>
                    </label>
                  </td></tr>
              {c.cliente_id_raw && <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Sede</td>
                  <td style={{fontSize:12}}>{c.cliente_id_raw}</td></tr>}
              <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Protocollo</td>
                  <td><strong>{c.numero_protocollo||'—'}</strong></td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Prot. interno</td>
                  <td>{editDate
                    ? <input type="text" className="form-control" style={{padding:'4px 8px'}} placeholder="Protocollo interno..." value={dateForm.protocollo_interno||''} onChange={e=>setDF(f=>({...f,protocollo_interno:e.target.value}))} />
                    : (c.protocollo_interno || '—')}</td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Inizio</td>
                  <td>{editDate
                    ? <input type="date" className="form-control" style={{padding:'4px 8px'}} value={dateForm.data_inizio} onChange={e=>setDF(f=>({...f,data_inizio:e.target.value}))} />
                    : fmtData(c.data_inizio)}</td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Scadenza</td>
                  <td>{editDate
                    ? <input type="date" className="form-control" style={{padding:'4px 8px'}} value={dateForm.data_fine} onChange={e=>setDF(f=>({...f,data_fine:e.target.value}))} />
                    : fmtData(c.data_fine)}</td></tr>
              <tr><td style={{color:'#9ca3af',padding:'4px 0'}}>Rinnovo auto</td>
                  <td>{c.rinnovo_automatico=='1'?'✅ Sì':'❌ No'}</td></tr>
            </tbody>
          </table>
          {editDate && (
            <button className="btn btn-primary btn-sm" style={{marginTop:10}} onClick={salvaDate} disabled={saving}>
              {saving?'Salvataggio…':'💾 Salva date'}
            </button>
          )}
          {c.note && <div style={{marginTop:10,background:'#f9fafb',borderRadius:6,padding:'8px 10px',fontSize:12}}>{c.note}</div>}
        </div>

        {/* CANONI */}
        <CanoniEditor contrattoId={c.id} righInit={righe} onSaved={async()=>{
          const r=await contrattiApi.getById(id);setData(r.data);
        }} />

        {/* ALLEGATO PDF */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">📎 Allegato contratto</span>
          </div>
          {c.allegato_url ? (
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <a href={`https://sosfire.quindi.pro${c.allegato_url}`} target="_blank" rel="noopener noreferrer"
                 className="btn btn-secondary">📄 Apri PDF allegato</a>
              <button className="btn btn-danger btn-sm" onClick={async()=>{
                await contrattiApi.update(id,{allegato_url:''});
                const r=await contrattiApi.getById(id);setData(r.data);
              }}>🗑 Rimuovi</button>
            </div>
          ) : (
            <div>
              <input type="file" accept=".pdf" id={`allegato-${c.id}`} style={{display:'none'}}
                onChange={async e=>{
                  const file=e.target.files[0]; if(!file) return;
                  const form=new FormData(); form.append('file',file);
                  try {
                    const res=await api.post(`/contratto/${c.id}/allegato`,form,{headers:{'Content-Type':'multipart/form-data'}});
                    if(res.data.success){const r=await contrattiApi.getById(id);setData(r.data);}
                  } catch(err){alert('Errore upload: '+(err.response?.data?.error||err.message));}
                }} />
              <label htmlFor={`allegato-${c.id}`} className="btn btn-secondary" style={{cursor:'pointer'}}>
                📤 Carica PDF contratto
              </label>
              <p style={{fontSize:12,color:'#9ca3af',marginTop:6}}>Max 10MB — solo PDF</p>
            </div>
          )}
        </div>

        {/* SEDI */}
        {sedi.length>0 && (
          <div className="card">
            <div className="card-header"><span className="card-title">📍 Sedi</span></div>
            {sedi.map((s,i)=>(
              <div key={i} style={{padding:'6px 0',borderBottom:'1px solid #f3f4f6',fontSize:13}}>
                {s.indirizzo}{s.note&&<span style={{color:'#9ca3af',fontSize:11,marginLeft:6}}>{s.note}</span>}
              </div>
            ))}
          </div>
        )}

        {/* INTERVENTI PIANIFICATI */}
        <div className="card" style={{gridColumn: sedi.length?'auto':'1 / -1'}}>
          <div className="card-header"><span className="card-title">📅 Interventi pianificati</span></div>
          {interventi.length===0 ? <p style={{color:'#9ca3af',fontSize:13}}>Nessun intervento pianificato.</p> : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead><tr style={{background:'#f9fafb'}}>
                <th style={{padding:'6px',textAlign:'left'}}>Data</th>
                <th style={{padding:'6px',textAlign:'center'}}>Tipo</th>
                <th style={{padding:'6px',textAlign:'center'}}>Stato</th>
                <th style={{padding:'6px'}}></th>
              </tr></thead>
              <tbody>
                {interventi.map((ci,i)=>(
                  <tr key={i} style={{borderBottom:'1px solid #f3f4f6'}}>
                    <td style={{padding:'6px'}}>{fmtData(ci.data_pianificata)}</td>
                    <td style={{padding:'6px',textAlign:'center'}}>
                      <span className="badge badge-blue">{ci.tipo}</span>
                    </td>
                    <td style={{padding:'6px',textAlign:'center'}}>
                      <span className={`badge ${ci.stato==='fatturato'?'badge-green':ci.stato==='eseguito'?'badge-orange':'badge-gray'}`}>
                        {ci.stato}
                      </span>
                    </td>
                    <td style={{padding:'6px'}}>
                      {(ci.stato==='pianificato'||ci.stato==='eseguito') && (
                        <button className="btn btn-primary btn-sm" onClick={()=>fatturaIntervento(ci)}>
                          🧾 Fattura
                        </button>
                      )}
                      {ci.stato==='fatturato' && <span style={{fontSize:11,color:'#155724'}}>✅ Fatturato</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── NUOVO CONTRATTO ────────────────────────────────────────────
function NuovoContratto({ onBack, onSaved }) {
  const [clienti, setClienti]   = useState([]);
  const [cercaC, setCercaC]     = useState('');
  const [clienteId, setClId]    = useState('');
  const [form, setForm]         = useState({
    data_inizio: new Date().toISOString().split('T')[0],
    durata_mesi: '12',
    rinnovo_automatico: '1',
    note: '',
  });
  const [righe, setRighe]       = useState([]);
  const [sedi, setSedi]         = useState([{indirizzo:'',note:''}]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [loadImp, setLoadImp]   = useState(false);

  useEffect(()=>{
    clientiApi.getAll().then(r=>setClienti(r.data?.data||r.data||[])).catch(console.error);
  },[]);

  const clientiFiltrati = clienti.filter(c => {
    if (!cercaC) return true;
    const parole = cercaC.toLowerCase().split(' ').filter(Boolean);
    const campi  = [c.nome, c.indirizzo, c.citta, c.zona, c.cap].filter(Boolean).join(' ').toLowerCase();
    return parole.every(p => campi.includes(p));
  })

  const selezionaCliente = async (c) => {
    setClId(c.id);
    setCercaC(c.nome);
    // Pre-popola sede con indirizzo cliente
    if (c.indirizzo) setSedi([{indirizzo:[c.indirizzo,c.citta].filter(Boolean).join(', '),note:''}]);
    // Carica impianti del cliente
    setLoadImp(true);
    try {
      const res = await api.get(`/cliente/${c.id}`);
      const d   = res.data;
      const nuoveRighe = [];
      Object.entries(IMPIANTI_LABEL).forEach(([tipo, label]) => {
        const lista = d[tipo] || [];
        if (lista.length>0) {
          nuoveRighe.push({
            descrizione: label.replace(/^.+? /,''),
            tipo_impianto: tipo,
            frequenza: ['gruppi_pompa','prove_flusso','dpi','gruppi_elettrogeni'].includes(tipo)?'annuale':'semestrale',
            prezzo_unitario: PREZZI_DEFAULT[tipo]||5,
            quantita: lista.length,
          });
        }
      });
      setRighe(nuoveRighe.length>0?nuoveRighe:[{descrizione:'',tipo_impianto:'',frequenza:'semestrale',prezzo_unitario:0,quantita:1}]);
    } catch(e){console.error(e);}
    finally{setLoadImp(false);}
  };

  const aggRiga  = () => setRighe(r=>[...r,{descrizione:'',tipo_impianto:'',frequenza:'semestrale',prezzo_unitario:0,quantita:1}]);
  const rimuRiga = i  => setRighe(r=>r.filter((_,j)=>j!==i));
  const setRiga  = (i,k,v) => setRighe(r=>r.map((x,j)=>j===i?{...x,[k]:v}:x));

  const aggSede  = () => setSedi(s=>[...s,{indirizzo:'',note:''}]);
  const rimuSede = i  => setSedi(s=>s.filter((_,j)=>j!==i));
  const setSede  = (i,k,v) => setSedi(s=>s.map((x,j)=>j===i?{...x,[k]:v}:x));

  const salva = async () => {
    if (!clienteId) { setError('Seleziona un cliente.'); return; }
    setSaving(true); setError('');
    try {
      const res = await contrattiApi.create({
        cliente_id: parseInt(clienteId),
        ...form,
        durata_mesi: parseInt(form.durata_mesi),
        rinnovo_automatico: parseInt(form.rinnovo_automatico),
        righe: righe.filter(r=>r.descrizione.trim()),
        sedi:  sedi.filter(s=>s.indirizzo.trim()),
      });
      if (res.data?.success) onSaved(res.data.id);
    } catch(e) {
      setError(e.response?.data?.error||'Errore nel salvataggio.');
    } finally { setSaving(false); }
  };

  const totTrim = righe.filter(r=>r.frequenza==='trimestrale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const totSem = righe.filter(r=>r.frequenza==='semestrale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const totAnn = righe.filter(r=>r.frequenza==='annuale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);

  return (
    <div className="page-content">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <button className="btn btn-secondary" onClick={onBack}>⬅ Indietro</button>
        <h2 style={{margin:0}}>➕ Nuovo contratto di manutenzione</h2>
      </div>

      {error && <div className="error-msg" style={{marginBottom:16}}>{error}</div>}

      <div style={{maxWidth:860}}>

        {/* CLIENTE */}
        <div className="card" style={{marginBottom:16}}>
          <div className="card-title" style={{marginBottom:12}}>👥 Cliente</div>
          <input className="form-control" placeholder="Cerca cliente per nome o città..."
            value={cercaC} onChange={e=>{setCercaC(e.target.value);setClId('');}}
            style={{marginBottom:8}} />
          {cercaC && !clienteId && (
            <div style={{border:'1px solid #e5e7eb',borderRadius:6,maxHeight:200,overflowY:'auto',background:'#fff'}}>
              {clientiFiltrati.slice(0,20).map(c=>(
                <div key={c.id} onClick={()=>selezionaCliente(c)}
                  style={{padding:'8px 12px',cursor:'pointer',borderBottom:'1px solid #f3f4f6',fontSize:13}}
                  onMouseEnter={e=>e.target.style.background='#f9fafb'}
                  onMouseLeave={e=>e.target.style.background=''}>
                  <strong>{c.nome}</strong>
                  {c.citta&&<span style={{color:'#9ca3af',marginLeft:6,fontSize:12}}>{c.citta}</span>}
                </div>
              ))}
              {clientiFiltrati.length===0&&<div style={{padding:12,color:'#9ca3af',fontSize:13}}>Nessun cliente trovato</div>}
            </div>
          )}
          {clienteId && <div style={{color:'#155724',fontSize:13,marginTop:4}}>✅ Cliente selezionato</div>}
          {loadImp && <div style={{color:'#6b7280',fontSize:12,marginTop:4}}>⏳ Caricamento impianti...</div>}
        </div>

        {/* DATI CONTRATTO */}
        <div className="card" style={{marginBottom:16}}>
          <div className="card-title" style={{marginBottom:12}}>📋 Dati contratto</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Data inizio</label>
              <input type="date" className="form-control" value={form.data_inizio}
                onChange={e=>setForm(f=>({...f,data_inizio:e.target.value}))} />
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Durata</label>
              <select className="form-control" value={form.durata_mesi}
                onChange={e=>setForm(f=>({...f,durata_mesi:e.target.value}))}>
                <option value="12">1 anno</option>
                <option value="24">2 anni</option>
                <option value="36">3 anni</option>
              </select>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Rinnovo automatico</label>
              <select className="form-control" value={form.rinnovo_automatico}
                onChange={e=>setForm(f=>({...f,rinnovo_automatico:e.target.value}))}>
                <option value="1">Sì</option>
                <option value="0">No</option>
              </select>
            </div>
            <div className="form-group" style={{margin:0,gridColumn:'1/-1'}}>
              <label className="form-label">Note</label>
              <textarea className="form-control" rows={2} value={form.note}
                onChange={e=>setForm(f=>({...f,note:e.target.value}))} />
            </div>
          </div>
        </div>

        {/* SEDI */}
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <span className="card-title">📍 Sedi</span>
            <button className="btn btn-secondary btn-sm" onClick={aggSede}>➕ Sede</button>
          </div>
          {sedi.map((s,i)=>(
            <div key={i} style={{display:'flex',gap:8,marginBottom:8}}>
              <input className="form-control" placeholder="Indirizzo sede" value={s.indirizzo}
                onChange={e=>setSede(i,'indirizzo',e.target.value)} style={{flex:1}} />
              <input className="form-control" placeholder="Note" value={s.note}
                onChange={e=>setSede(i,'note',e.target.value)} style={{width:130}} />
              {sedi.length>1 && <button className="btn btn-danger btn-sm" onClick={()=>rimuSede(i)}>✕</button>}
            </div>
          ))}
        </div>

        {/* RIGHE IMPIANTI */}
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <span className="card-title">🔧 Impianti e prezzi</span>
            <button className="btn btn-secondary btn-sm" onClick={aggRiga}>➕ Riga</button>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#f9fafb'}}>
              <th style={{padding:'6px',textAlign:'left'}}>Descrizione</th>
              <th style={{padding:'6px',textAlign:'center',width:120}}>Frequenza</th>
              <th style={{padding:'6px',textAlign:'center',width:60}}>Qtà</th>
              <th style={{padding:'6px',textAlign:'right',width:90}}>€/cad</th>
              <th style={{width:32}}></th>
            </tr></thead>
            <tbody>
              {righe.map((r,i)=>(
                <tr key={i}>
                  <td style={{padding:'4px 4px'}}>
                    <input className="form-control" value={r.descrizione}
                      onChange={e=>setRiga(i,'descrizione',e.target.value)}
                      style={{fontSize:12,padding:'5px 8px'}} />
                  </td>
                  <td style={{padding:'4px'}}>
                    <select className="form-control" value={r.frequenza}
                      onChange={e=>setRiga(i,'frequenza',e.target.value)}
                      style={{fontSize:12,padding:'5px 8px'}}>
                      <option value="trimestrale">Trimestrale</option>
                      <option value="semestrale">Semestrale</option>
                      <option value="annuale">Annuale</option>
                      <option value="una_tantum">Una tantum</option>
                    </select>
                  </td>
                  <td style={{padding:'4px'}}>
                    <input type="number" className="form-control" value={r.quantita} min={1}
                      onChange={e=>setRiga(i,'quantita',e.target.value)}
                      style={{fontSize:12,padding:'5px 8px',textAlign:'center'}} />
                  </td>
                  <td style={{padding:'4px'}}>
                    <input type="number" className="form-control" value={r.prezzo_unitario} min={0} step={0.01}
                      onChange={e=>setRiga(i,'prezzo_unitario',e.target.value)}
                      style={{fontSize:12,padding:'5px 8px',textAlign:'right'}} />
                  </td>
                  <td style={{padding:'4px'}}>
                    <button className="btn btn-danger btn-sm" onClick={()=>rimuRiga(i)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(totSem>0||totAnn>0) && (
            <div style={{marginTop:10,textAlign:'right',fontSize:13}}>
              {totTrim>0&&<div>Totale trimestrale: <strong style={{color:'#b30000'}}>{eur(totTrim)}</strong></div>}
              {totSem>0&&<div>Totale semestrale: <strong style={{color:'#b30000'}}>{eur(totSem)}</strong></div>}
              {totAnn>0&&<div>Totale annuale: <strong style={{color:'#b30000'}}>{eur(totAnn)}</strong></div>}
            </div>
          )}
        </div>

        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-primary" onClick={salva} disabled={saving}>
            {saving?'Salvataggio…':'💾 Crea contratto'}
          </button>
          <button className="btn btn-secondary" onClick={onBack}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── EDITOR CANONI ──────────────────────────────────────────────
function CanoniEditor({ contrattoId, righInit, onSaved }) {
  const [righe, setRighe]   = useState(righInit || []);
  const [editMode, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRighe(righInit || []); }, [righInit]);

  const setRiga = (i,k,v) => setRighe(r => r.map((x,j) => j===i ? {...x,[k]:v} : x));
  const aggRiga = () => setRighe(r => [...r, {descrizione:'',tipo_impianto:'',frequenza:'semestrale',quantita:1,prezzo_unitario:0}]);
  const rimuRiga = i => setRighe(r => r.filter((_,j) => j!==i));

  const salva = async () => {
    setSaving(true);
    try {
      await api.post(`/contratto/${contrattoId}/righe`, righe);
      setEdit(false);
      if (onSaved) await onSaved();
    } catch(e) { alert('Errore: ' + e.message); }
    finally { setSaving(false); }
  };

  const totTrim = righe.filter(r=>r.frequenza==='trimestrale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const totSem = righe.filter(r=>r.frequenza==='semestrale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);
  const totAnn = righe.filter(r=>r.frequenza==='annuale').reduce((s,r)=>s+parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1),0);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">💰 Canoni</span>
        <div style={{display:'flex',gap:6}}>
          {editMode && <button className="btn btn-secondary btn-sm" onClick={aggRiga}>➕ Riga</button>}
          <button className="btn btn-secondary btn-sm" onClick={()=>setEdit(v=>!v)}>
            {editMode ? '✕ Annulla' : '✏️ Modifica'}
          </button>
          {editMode && <button className="btn btn-primary btn-sm" onClick={salva} disabled={saving}>
            {saving?'…':'💾 Salva'}
          </button>}
        </div>
      </div>

      {righe.length===0 && !editMode && <p style={{color:'#9ca3af',fontSize:13}}>Nessuna riga definita.</p>}

      {editMode ? (
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead><tr style={{background:'#f9fafb'}}>
            <th style={{padding:'6px',textAlign:'left'}}>Descrizione</th>
            <th style={{padding:'6px',width:120}}>Frequenza</th>
            <th style={{padding:'6px',width:60,textAlign:'center'}}>Qtà</th>
            <th style={{padding:'6px',width:90,textAlign:'right'}}>€/cad</th>
            <th style={{width:32}}></th>
          </tr></thead>
          <tbody>
            {righe.map((r,i)=>(
              <tr key={i}>
                <td style={{padding:'3px'}}>
                  <input className="form-control" value={r.descrizione||''}
                    onChange={e=>setRiga(i,'descrizione',e.target.value)}
                    style={{fontSize:12,padding:'4px 6px'}} />
                </td>
                <td style={{padding:'3px'}}>
                  <select className="form-control" value={r.frequenza||'semestrale'}
                    onChange={e=>setRiga(i,'frequenza',e.target.value)}
                    style={{fontSize:12,padding:'4px 6px'}}>
                    <option value="trimestrale">Trimestrale</option>
                    <option value="trimestrale">Trimestrale</option>
                    <option value="semestrale">Semestrale</option>
                    <option value="annuale">Annuale</option>
                    <option value="una_tantum">Una tantum</option>
                  </select>
                </td>
                <td style={{padding:'3px'}}>
                  <input type="number" className="form-control" value={r.quantita||1} min={1}
                    onChange={e=>setRiga(i,'quantita',e.target.value)}
                    style={{fontSize:12,padding:'4px 6px',textAlign:'center'}} />
                </td>
                <td style={{padding:'3px'}}>
                  <input type="number" className="form-control" value={r.prezzo_unitario||0} step={0.01} min={0}
                    onChange={e=>setRiga(i,'prezzo_unitario',e.target.value)}
                    style={{fontSize:12,padding:'4px 6px',textAlign:'right'}} />
                </td>
                <td style={{padding:'3px'}}>
                  <button className="btn btn-danger btn-sm" onClick={()=>rimuRiga(i)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        righe.length > 0 && (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#f9fafb'}}>
              <th style={{padding:'6px',textAlign:'left'}}>Descrizione</th>
              <th style={{padding:'6px',textAlign:'center'}}>Freq.</th>
              <th style={{padding:'6px',textAlign:'right'}}>Qtà</th>
              <th style={{padding:'6px',textAlign:'right'}}>Prezzo</th>
              <th style={{padding:'6px',textAlign:'right'}}>Totale</th>
            </tr></thead>
            <tbody>
              {righe.map((r,i)=>(
                <tr key={i} style={{borderBottom:'1px solid #f3f4f6'}}>
                  <td style={{padding:'6px'}}>{r.descrizione}</td>
                  <td style={{padding:'6px',textAlign:'center',fontSize:11,color:'#6b7280'}}>{r.frequenza}</td>
                  <td style={{padding:'6px',textAlign:'right'}}>{r.quantita}</td>
                  <td style={{padding:'6px',textAlign:'right'}}>{eur(r.prezzo_unitario)}</td>
                  <td style={{padding:'6px',textAlign:'right',fontWeight:600}}>{eur(parseFloat(r.prezzo_unitario||0)*parseFloat(r.quantita||1))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {totTrim>0 && <tr><td colSpan={4} style={{padding:'6px',textAlign:'right',fontWeight:600,borderTop:'2px solid #e5e7eb'}}>Totale trimestrale:</td>
                <td style={{padding:'6px',textAlign:'right',fontWeight:700,color:'#b30000',borderTop:'2px solid #e5e7eb'}}>{eur(totTrim)}</td></tr>}
              {totSem>0 && <tr><td colSpan={4} style={{padding:'6px',textAlign:'right',fontWeight:600,borderTop:'2px solid #e5e7eb'}}>Totale semestrale:</td>
                <td style={{padding:'6px',textAlign:'right',fontWeight:700,color:'#b30000',borderTop:'2px solid #e5e7eb'}}>{eur(totSem)}</td></tr>}
              {totAnn>0 && <tr><td colSpan={4} style={{padding:'6px',textAlign:'right',fontWeight:600}}>Totale annuale:</td>
                <td style={{padding:'6px',textAlign:'right',fontWeight:700,color:'#b30000'}}>{eur(totAnn)}</td></tr>}
            </tfoot>
          </table>
        )
      )}
    </div>
  );
}

// ── REFERENTE EDITOR ───────────────────────────────────────────
function ReferenteEditor({ clienteId, campo = 'contatto' }) {
  const [valore,  setValore]  = useState('');
  const [editMode,setEdit]    = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    if (!clienteId) return;
    api.get(`/clienti/${clienteId}`).then(r => {
      const c = r.data?.data || r.data;
      setValore(c?.[campo] || '');
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [clienteId, campo]);

  const salva = async () => {
    setSaving(true);
    try {
      await api.put(`/clienti/${clienteId}`, { [campo]: valore });
      setEdit(false);
    } catch(e) { alert('Errore: ' + (e.response?.data?.error||e.message)); }
    finally { setSaving(false); }
  };

  if (!loaded) return <span style={{color:'#9ca3af',fontSize:12}}>...</span>;

  if (editMode) return (
    <div style={{display:'flex',gap:6,alignItems:'center'}}>
      <input className="form-control" value={valore}
        onChange={e=>setValore(e.target.value)}
        style={{fontSize:12,padding:'3px 6px',flex:1}}
        placeholder={campo==='telefono'?'Telefono...':'Nome referente...'} />
      <button className="btn btn-primary btn-sm" onClick={salva} disabled={saving}>
        {saving?'...':'💾'}
      </button>
      <button className="btn btn-secondary btn-sm" onClick={()=>setEdit(false)}>✕</button>
    </div>
  );

  return (
    <div style={{display:'flex',gap:8,alignItems:'center'}}>
      <span style={{fontSize:13}}>{valore || <em style={{color:'#9ca3af'}}>Non specificato</em>}</span>
      <button className="btn btn-secondary btn-sm" style={{fontSize:10,padding:'1px 6px'}}
        onClick={()=>setEdit(true)}>✏️</button>
    </div>
  );
}

// ── INDIRIZZO CLIENTE (sola lettura) ──────────────────────────
function IndirizzoCliente({ clienteId }) {
  const [cliente, setCliente] = useState(null);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    if (!clienteId) { setLoaded(true); return; }
    api.get(`/clienti/${clienteId}`).then(r => {
      setCliente(r.data?.data || r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [clienteId]);

  if (!loaded) return <span style={{color:'#9ca3af',fontSize:12}}>...</span>;
  if (!cliente) return <em style={{color:'#9ca3af'}}>—</em>;

  const parti = [
    cliente.indirizzo,
    [cliente.cap, cliente.citta].filter(Boolean).join(' '),
    cliente.provincia ? `(${cliente.provincia})` : '',
  ].filter(Boolean);

  if (parti.length === 0) return <em style={{color:'#9ca3af'}}>Non specificato</em>;

  return (
    <div style={{fontSize:13,lineHeight:1.5}}>
      {parti.map((p, i) => <div key={i}>{p}</div>)}
    </div>
  );
}
